import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'coachhub_browser_push_token';
const FIREBASE_SCRIPTS = [
  'https://www.gstatic.com/firebasejs/10.14.1/firebase-app-compat.js',
  'https://www.gstatic.com/firebasejs/10.14.1/firebase-messaging-compat.js',
];

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyAMyR_sJSy2lzFGX8IcjM-6P5PnHJ0XxsY',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'coachhub-a17e4.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'coachhub-a17e4',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'coachhub-a17e4.firebasestorage.app',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '393186836887',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:393186836887:android:9d2e50c25bc9d2600204d2',
};

let sdkPromise;
let foregroundListenerAttached = false;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="' + src + '"]');
    if (existing) {
      if (existing.dataset.loaded === 'true') resolve();
      else {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      }
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.loaded = 'false';
    script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
    script.onerror = () => reject(new Error('Could not load ' + src));
    document.head.appendChild(script);
  });
}

async function loadFirebaseMessaging() {
  if (!sdkPromise) {
    sdkPromise = (async () => {
      for (const src of FIREBASE_SCRIPTS) await loadScript(src);
      if (!window.firebase?.apps?.length) window.firebase.initializeApp(firebaseConfig);
      return window.firebase.messaging();
    })();
  }
  return sdkPromise;
}

export function browserPushSupport() {
  if (!window.isSecureContext) return { supported: false, reason: 'secure_context_required' };
  if (!('Notification' in window)) return { supported: false, reason: 'notifications_unsupported' };
  if (!('serviceWorker' in navigator)) return { supported: false, reason: 'service_worker_unsupported' };
  if (!('PushManager' in window)) return { supported: false, reason: 'push_unsupported' };
  return { supported: true, permission: Notification.permission };
}

async function serviceWorkerRegistration() {
  const registration = await navigator.serviceWorker.getRegistration('/') ||
    await navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' });
  await navigator.serviceWorker.ready;
  return registration;
}

function attachForegroundListener(messaging) {
  if (foregroundListenerAttached) return;
  foregroundListenerAttached = true;
  messaging.onMessage((payload) => {
    const notification = payload?.notification || {};
    const data = payload?.data || {};
    const title = notification.title || data.title || 'CoachHub';
    const body = notification.body || data.body || data.message || 'You have a new notification';
    window.dispatchEvent(new CustomEvent('coachhub:push-message', { detail: { title, body, data } }));
    if (Notification.permission === 'granted') {
      navigator.serviceWorker.ready
        .then((registration) => registration.showNotification(title, {
          body,
          icon: '/icons/icon-192x192.png',
          badge: '/icons/icon-96x96.png',
          data,
          tag: data.notification_id || data.type || 'coachhub-foreground',
        }))
        .catch((error) => console.warn('[Push] Foreground display failed:', error));
    }
  });
}

export async function syncBrowserPushToken({ requestPermission = false } = {}) {
  const support = browserPushSupport();
  if (!support.supported) return support;
  let permission = Notification.permission;
  if (permission === 'default' && requestPermission) permission = await Notification.requestPermission();
  if (permission !== 'granted') return { supported: true, permission, registered: false };

  const registration = await serviceWorkerRegistration();
  const messaging = await loadFirebaseMessaging();
  attachForegroundListener(messaging);
  const options = { serviceWorkerRegistration: registration };
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (vapidKey) options.vapidKey = vapidKey;
  const token = await messaging.getToken(options);
  if (!token) throw new Error('Firebase did not return a browser push token');
  await axios.post(API + '/notifications/push/register', { token, device_type: 'web' });
  localStorage.setItem(TOKEN_KEY, token);
  return { supported: true, permission, registered: true, token };
}

export async function unregisterBrowserPush() {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;
  try {
    await axios.delete(API + '/notifications/push/unregister', { params: { token } });
  } finally {
    localStorage.removeItem(TOKEN_KEY);
    try {
      const messaging = await loadFirebaseMessaging();
      await messaging.deleteToken(token);
    } catch {}
  }
}
